import React, { useState, useRef, useEffect } from 'react';

const appleSpring = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

interface WarningFormBoxProps {
  onCancelClick?: () => void;
  onConfirmSubmit?: (data: { imie: string; nazwisko: string; stanowisko: string }) => void;
  employeeName?: string;
  warningType?: 'upomnienie' | 'nagana';
}

const MicIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#ff4444' : 'rgba(255,255,255,0.5)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="11" rx="3"/>
    <path d="M5 10a7 7 0 0 0 14 0"/>
    <line x1="12" y1="19" x2="12" y2="22"/>
    <line x1="9" y1="22" x2="15" y2="22"/>
  </svg>
);

export const WarningFormBox: React.FC<WarningFormBoxProps> = ({
  onCancelClick,
  onConfirmSubmit,
  employeeName = '',
  warningType = 'upomnienie',
}) => {
  const [imie, setImie] = useState(employeeName);
  const [nazwisko, setNazwisko] = useState('');
  const [stanowisko, setStanowisko] = useState('');
  const [isCancelPushed, setIsCancelPushed] = useState(false);
  const [isConfirmPushed, setIsConfirmPushed] = useState(false);
  const [activeField, setActiveField] = useState<'imie' | 'nazwisko' | 'stanowisko' | null>(null);
  const speechRef = useRef<any>(null);

  useEffect(() => {
    setImie(employeeName);
  }, [employeeName]);

  const startDictation = (field: 'imie' | 'nazwisko' | 'stanowisko') => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) return;
    if (speechRef.current) { speechRef.current.stop(); speechRef.current = null; }
    setActiveField(field);
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'pl-PL';
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (e: any) => {
      const text = e.results[0][0].transcript.trim();
      if (field === 'imie') setImie(prev => (prev ? prev + ' ' : '') + text);
      if (field === 'nazwisko') setNazwisko(prev => (prev ? prev + ' ' : '') + text);
      if (field === 'stanowisko') setStanowisko(prev => (prev ? prev + ' ' : '') + text);
    };
    rec.onend = () => { setActiveField(null); speechRef.current = null; };
    rec.onerror = () => { setActiveField(null); speechRef.current = null; };
    rec.start();
    speechRef.current = rec;
  };

  const stopDictation = () => {
    speechRef.current?.stop();
    speechRef.current = null;
    setActiveField(null);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'rgba(255,255,255,0.9)',
    fontSize: '15px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: 300,
    letterSpacing: '0.5px',
  };

  const rowStyle = (pushed: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    padding: '0 18px',
    height: '41px',
    background: 'rgba(0,0,0,0.15)',
    borderRadius: '21px',
    border: '1px solid rgba(255,255,255,0.08)',
    transition: `transform 120ms ${appleSpring}`,
    transform: pushed ? 'scale(0.97)' : 'scale(1)',
    gap: '8px',
  });

  return (
    <div style={{
      width: '360px',
      borderRadius: '33px',
      overflow: 'hidden',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      background: 'linear-gradient(180deg, rgba(153,0,0,0.32) 5%, rgba(255,0,0,0.38) 56%, rgba(153,0,0,0.32) 100%)',
      border: '1px solid rgba(255,80,80,0.18)',
      boxShadow: '0 8px 48px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
      padding: '22px 22px 0 22px',
      userSelect: 'none',
    }}>

      {/* Tytuł */}
      <div style={{
        textAlign: 'center',
        color: 'rgba(255,255,255,0.55)',
        fontSize: '11px',
        fontFamily: '"Inter", sans-serif',
        fontWeight: 400,
        letterSpacing: '2.5px',
        textTransform: 'uppercase',
        marginBottom: '16px',
      }}>
        {warningType === 'upomnienie' ? 'Upomnienie' : 'Nagana'}
      </div>

      {/* Pole: Imię */}
      <div style={{ marginBottom: '10px' }}>
        <div style={rowStyle(false)}>
          <input
            style={inputStyle}
            placeholder="Imię"
            value={imie}
            onChange={e => setImie(e.target.value)}
          />
          <button
            onMouseDown={() => activeField === 'imie' ? stopDictation() : startDictation('imie')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
          >
            <MicIcon active={activeField === 'imie'} />
          </button>
        </div>
      </div>

      {/* Pole: Nazwisko */}
      <div style={{ marginBottom: '10px' }}>
        <div style={rowStyle(false)}>
          <input
            style={inputStyle}
            placeholder="Nazwisko"
            value={nazwisko}
            onChange={e => setNazwisko(e.target.value)}
          />
          <button
            onMouseDown={() => activeField === 'nazwisko' ? stopDictation() : startDictation('nazwisko')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
          >
            <MicIcon active={activeField === 'nazwisko'} />
          </button>
        </div>
      </div>

      {/* Pole: Stanowisko */}
      <div style={{ marginBottom: '18px' }}>
        <div style={rowStyle(false)}>
          <input
            style={inputStyle}
            placeholder="Stanowisko"
            value={stanowisko}
            onChange={e => setStanowisko(e.target.value)}
          />
          <button
            onMouseDown={() => activeField === 'stanowisko' ? stopDictation() : startDictation('stanowisko')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
          >
            <MicIcon active={activeField === 'stanowisko'} />
          </button>
        </div>
      </div>

      {/* Dolna belka: Anuluj + Zatwierdź */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: '22px',
        gap: '12px',
      }}>
        {/* Anuluj */}
        <button
          onMouseDown={(e) => { e.stopPropagation(); setIsCancelPushed(true); }}
          onMouseUp={(e) => { e.stopPropagation(); setIsCancelPushed(false); onCancelClick?.(); }}
          onMouseLeave={() => setIsCancelPushed(false)}
          onTouchStart={(e) => { e.stopPropagation(); setIsCancelPushed(true); }}
          onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setIsCancelPushed(false); onCancelClick?.(); }}
          onClick={(e) => { e.stopPropagation(); }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: isCancelPushed ? '#ffffff' : 'rgba(255,255,255,0.45)',
            fontSize: '14px',
            fontFamily: '"Inter", sans-serif',
            fontWeight: 300,
            letterSpacing: '1px',
            transition: `transform 120ms ${appleSpring}, color 120ms ease`,
            transform: isCancelPushed ? 'scale(0.93)' : 'scale(1)',
            padding: '6px 12px',
          }}
        >
          Anuluj
        </button>

        {/* Zatwierdź — czerwona kula */}
        <button
          onMouseDown={(e) => { e.stopPropagation(); setIsConfirmPushed(true); }}
          onMouseUp={(e) => { e.stopPropagation(); setIsConfirmPushed(false); onConfirmSubmit?.({ imie, nazwisko, stanowisko }); }}
          onMouseLeave={() => setIsConfirmPushed(false)}
          onTouchStart={(e) => { e.stopPropagation(); setIsConfirmPushed(true); }}
          onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setIsConfirmPushed(false); onConfirmSubmit?.({ imie, nazwisko, stanowisko }); }}
          onClick={(e) => { e.stopPropagation(); }}
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            border: isConfirmPushed ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(255,255,255,0.1)',
            background: isConfirmPushed
              ? 'radial-gradient(50% 50% at 50% 50%, rgba(255,0,0,0.85) 40%, rgba(153,0,0,0.4) 100%)'
              : 'radial-gradient(50% 50% at 50% 50%, rgba(255,0,0,0.56) 39%, rgba(153,0,0,0.4) 100%)',
            boxShadow: isConfirmPushed
              ? '0 0 20px rgba(255,0,0,0.6), inset 0 1px 2px rgba(255,255,255,0.3)'
              : '0 4px 16px rgba(0,0,0,0.4), inset 0 1px 1px rgba(255,255,255,0.15)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: `transform 150ms ${appleSpring}, box-shadow 150ms ease`,
            transform: isConfirmPushed ? 'scale(0.93)' : 'scale(1)',
            flexShrink: 0,
          }}
        >
          {/* Checkmark */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
      </div>

    </div>
  );
};
